/**
 * NotificationBell Component
 * Shows notification count and alerts dropdown in header
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, Euro, FileWarning, Wrench, Truck, Mail, Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alert type configuration
const ALERT_CONFIG = {
  DELINQUENCY: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  PAYMENT_DUE: { icon: Euro, color: 'text-orange-600', bg: 'bg-orange-50' },
  F24_DUE: { icon: FileWarning, color: 'text-orange-600', bg: 'bg-orange-50' },
  COMMUNICATION_URGENT: { icon: Mail, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  INCIDENT_REPORT: { icon: Wrench, color: 'text-purple-600', bg: 'bg-purple-50' },
  CONTRACT_EXPIRY: { icon: Truck, color: 'text-blue-600', bg: 'bg-blue-50' }
};

const PRIORITY_COLORS = {
  CRITICAL: 'bg-red-500',
  HIGH: 'bg-orange-500',
  MEDIUM: 'bg-yellow-500',
  LOW: 'bg-blue-500'
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    
    try {
      const [alertsRes, countRes] = await Promise.all([
        fetch(`${API_URL}/api/notifications/alerts`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data.alerts || []);
      }
      
      if (countRes.ok) {
        const data = await countRes.json();
        setUnreadCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleAlertClick = (alert) => {
    setOpen(false);
    if (alert.action_url) {
      navigate(alert.action_url);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const totalCount = alerts.length + unreadCount;
  const hasCritical = alerts.some(a => a.priority === 'CRITICAL');

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`relative ${hasCritical ? 'animate-pulse' : ''}`}
        >
          <Bell className={`h-5 w-5 ${hasCritical ? 'text-red-500' : 'text-slate-600'}`} />
          {totalCount > 0 && (
            <span className={`absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs text-white flex items-center justify-center ${hasCritical ? 'bg-red-500' : 'bg-orange-500'}`}>
              {totalCount > 9 ? '9+' : totalCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifiche e Avvisi</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 text-xs"
              onClick={handleMarkAllRead}
            >
              <Check className="h-3 w-3 mr-1" />
              Segna lette
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {alerts.length === 0 && unreadCount === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p>Nessuna notifica</p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {/* Critical Alerts First */}
            {alerts.filter(a => a.priority === 'CRITICAL').map((alert) => {
              const config = ALERT_CONFIG[alert.type] || ALERT_CONFIG.COMMUNICATION_URGENT;
              const Icon = config.icon;
              
              return (
                <DropdownMenuItem 
                  key={alert.id}
                  className={`flex items-start gap-3 p-3 cursor-pointer ${config.bg} border-l-4 border-red-500`}
                  onClick={() => handleAlertClick(alert)}
                >
                  <div className={`p-1.5 rounded-full bg-white`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{alert.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{alert.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </DropdownMenuItem>
              );
            })}
            
            {/* Other Alerts */}
            {alerts.filter(a => a.priority !== 'CRITICAL').map((alert) => {
              const config = ALERT_CONFIG[alert.type] || ALERT_CONFIG.COMMUNICATION_URGENT;
              const Icon = config.icon;
              const priorityColor = PRIORITY_COLORS[alert.priority] || PRIORITY_COLORS.LOW;
              
              return (
                <DropdownMenuItem 
                  key={alert.id}
                  className="flex items-start gap-3 p-3 cursor-pointer"
                  onClick={() => handleAlertClick(alert)}
                >
                  <div className={`p-1.5 rounded-full ${config.bg}`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{alert.title}</p>
                      <span className={`h-2 w-2 rounded-full ${priorityColor}`} />
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{alert.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </DropdownMenuItem>
              );
            })}
          </div>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          className="justify-center text-sm text-blue-600"
          onClick={() => { setOpen(false); navigate('/notifications'); }}
        >
          Vedi tutte le notifiche
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
